import { useCallback, useEffect, useRef, useState } from 'react'
import { triggerPensionCalculationForCurrentUser } from '../services/notifier'
import {
  hasPensionCalculationForLatestUtm,
  hasStalePendingPensionSnapshot,
} from '../services/pension'
import { getPensionConfigInputsKey } from '../config/settings/constants'
import { toUserFacingError } from '../config/resources/errors'
import { getLogger } from '../core/debuggers'

const logger = getLogger('useEnsurePensionCalculation')

const POLL_INTERVAL_MS = 1500
const POLL_TIMEOUT_MS = 60000

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

/**
 * Ensures the logged-in user has a pension calculation for the latest UTM month
 * and that pending current/upcoming snapshots match the active config.
 */
export default function useEnsurePensionCalculation({
  userId,
  hasConfig,
  configReady,
  force = false,
  enabled = true,
  config = null,
  configMethod = null,
  onCompleted,
}) {
  const [status, setStatus] = useState('idle') // idle | checking | generating | ready | error
  const [error, setError] = useState(null)
  const [attempt, setAttempt] = useState(0)
  const onCompletedRef = useRef(onCompleted)
  const ranKeyRef = useRef(null)
  const configKey = getPensionConfigInputsKey(config)
  const methodKey = configMethod || config?.method || 'none'

  useEffect(() => {
    onCompletedRef.current = onCompleted
  }, [onCompleted])

  const isCalculationCurrent = useCallback(async () => {
    const alreadyExists = await hasPensionCalculationForLatestUtm(userId)
    if (!alreadyExists) return false
    const stale = await hasStalePendingPensionSnapshot(userId, config)
    return !stale
  }, [userId, config])

  const run = useCallback(async () => {
    if (!enabled || !userId || !configReady) return
    if (!hasConfig) {
      setStatus('idle')
      setError(null)
      return
    }

    const runKey = `${userId}:${force}:${attempt}:${methodKey}:${configKey}`
    if (ranKeyRef.current === runKey) return
    ranKeyRef.current = runKey

    setStatus(force ? 'generating' : 'checking')
    setError(null)

    try {
      const alreadyCurrent = force ? false : await isCalculationCurrent()

      if (alreadyCurrent) {
        setStatus('ready')
        return
      }

      setStatus('generating')
      await triggerPensionCalculationForCurrentUser()

      const startedAt = Date.now()
      while (Date.now() - startedAt < POLL_TIMEOUT_MS) {
        const ready = await isCalculationCurrent()
        if (ready) {
          setStatus('ready')
          if (typeof onCompletedRef.current === 'function') {
            onCompletedRef.current()
          }
          return
        }
        await sleep(POLL_INTERVAL_MS)
      }

      throw new Error(
        'Está tardando más de lo esperado. Puedes reintentar en unos segundos.'
      )
    } catch (err) {
      logger.error(err)
      setError(
        toUserFacingError(
          err,
          'No pudimos preparar tu monto estimado. Inténtalo de nuevo en unos segundos.'
        )
      )
      setStatus('error')
    }
  }, [
    enabled,
    userId,
    configReady,
    hasConfig,
    force,
    attempt,
    methodKey,
    configKey,
    isCalculationCurrent,
  ])

  useEffect(() => {
    run()
  }, [run])

  const retry = useCallback(() => {
    ranKeyRef.current = null
    setAttempt((value) => value + 1)
  }, [])

  return {
    status,
    error,
    generating: status === 'generating',
    retry,
  }
}
